import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Checkbox, FormControl,
    FormControlLabel, InputLabel, MenuItem, Paper, Select,
    Table, TableBody, TableCell, TableContainer, TableRow, TextField, Typography,
} from "@mui/material"
import { Control, Controller, UseFormGetValues } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { evaluateArithmetic } from "@/lib/arithmetic"
import MixSection from "./MixSection"
import { IItem, IOrder, SetValueFn } from "./types"

export default function ExtrusionSection({ control, setValue, getValues, treatment, items, loadingItems }: {
    control: Control<IOrder>
    setValue: SetValueFn
    getValues: UseFormGetValues<IOrder>
    treatment: boolean
    items: IItem[]
    loadingItems: boolean
}) {
    const { t } = useTranslation()

    return (
        <Accordion defaultExpanded disableGutters variant="outlined" sx={{ mb: 2, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                <Typography sx={{ fontWeight: "bold" }}>{t("orders.form.extrusion")}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2, mb: 2 }}>
                    <Controller control={control} name="extrusion_extruder_number"
                        render={({ field }) => <TextField {...field} label={t("orders.form.extruderNo")} size="small" type="number" fullWidth />}
                    />
                    <Controller control={control} name="extrusion_mold"
                        render={({ field }) => <TextField {...field} label={t("orders.form.mold")} size="small" type="number" fullWidth />}
                    />
                    <Controller control={control} name="extrusion_width_formula"
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label={t("orders.form.widthFormula")}
                                size="small"
                                fullWidth
                                onChange={e => {
                                    field.onChange(e)
                                    const result = evaluateArithmetic(e.target.value)
                                    if (result !== null) {
                                        setValue("extrusion_width", result)
                                        setValue("extrusion_width_min", result)
                                        setValue("extrusion_width_max", result + 0.5)
                                    }
                                }}
                            />
                        )}
                    />
                    <Controller control={control} name="extrusion_width"
                        render={({ field }) => (
                            <TextField {...field} label={t("orders.form.widthCms")} size="small" type="number" fullWidth
                                onChange={e => {
                                    field.onChange(e)
                                    const w = Number(e.target.value)
                                    setValue("extrusion_width_min", w)
                                    setValue("extrusion_width_max", w + 0.5)
                                }}
                            />
                        )}
                    />
                    <Controller control={control} name="extrusion_bellows"
                        render={({ field }) => <TextField {...field} label={t("orders.form.bellowsCms")} size="small" type="number" fullWidth />}
                    />
                    <Controller control={control} name="extrusion_pigment"
                        render={({ field: { value, ...field } }) => (
                            <FormControlLabel label={t("orders.form.pigment")} control={<Checkbox {...field} checked={value} size="small" />} />
                        )}
                    />
                    <Controller control={control} name="extrusion_treatment"
                        render={({ field: { value, ...field } }) => (
                            <FormControlLabel label={t("orders.form.treatment")} control={<Checkbox {...field} checked={value} size="small" />} />
                        )}
                    />
                    {treatment && (
                        <Controller control={control} name="extrusion_treatment_type"
                            render={({ field }) => (
                                <FormControl size="small" fullWidth>
                                    <InputLabel>{t("orders.form.treatmentType")}</InputLabel>
                                    <Select {...field} label={t("orders.form.treatmentType")}>
                                        <MenuItem value={0}>{t("orders.form.none")}</MenuItem>
                                        <MenuItem value={1}>1C</MenuItem>
                                        <MenuItem value={2}>2C</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                        />
                    )}
                </Box>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell variant="head">{t("orders.form.widthCms")}</TableCell>
                                <TableCell><Controller control={control} name="extrusion_width" render={({ field: { value } }) => <span>{Number(value).toFixed(2)} cms</span>} /></TableCell>
                                <TableCell variant="head">{t("orders.form.minWidth")}</TableCell>
                                <TableCell><Controller control={control} name="extrusion_width_min" render={({ field: { value } }) => <span>{Number(value).toFixed(2)} cms</span>} /></TableCell>
                                <TableCell variant="head">{t("orders.form.maxWidth")}</TableCell>
                                <TableCell><Controller control={control} name="extrusion_width_max" render={({ field: { value } }) => <span>{Number(value).toFixed(2)} cms</span>} /></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell variant="head">{t("orders.form.caliber")}</TableCell>
                                <TableCell><Controller control={control} name="extrusion_caliber" render={({ field: { value } }) => <span>{Number(value).toFixed(2)} Mil/pg</span>} /></TableCell>
                                <TableCell variant="head">{t("orders.form.minCaliber")}</TableCell>
                                <TableCell><Controller control={control} name="extrusion_caliber_min" render={({ field: { value } }) => <span>{Number(value).toFixed(2)} Mil/pg</span>} /></TableCell>
                                <TableCell variant="head">{t("orders.form.maxCaliber")}</TableCell>
                                <TableCell><Controller control={control} name="extrusion_caliber_max" render={({ field: { value } }) => <span>{Number(value).toFixed(2)} Mil/pg</span>} /></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
                    <MixSection
                        control={control} setValue={setValue} getValues={getValues}
                        mixType="primary" totalPctName="order_mix_total_percentage"
                        totalValName="order_mix_total_value" title={t("orders.form.primaryMix")}
                        items={items} loadingItems={loadingItems}
                    />
                    <MixSection
                        control={control} setValue={setValue} getValues={getValues}
                        mixType="secondary" totalPctName="order_secondary_mix_total_percentage"
                        totalValName="order_secondary_mix_total_value" title={t("orders.form.secondaryMix")}
                        items={items} loadingItems={loadingItems}
                    />
                </Box>
            </AccordionDetails>
        </Accordion>
    )
}
